import getMetricsData from '@api/metricContainer';
import { get } from 'lodash';
import instance from '@api/axiosInstance';
import { getPercentageMetricByKey, getPercentileMetrics } from '@src/utils/metrics/metricHelpers';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import { createQuery } from '@src/components/widget-panel/utils/query';
import useQueryParams from '../../hooks/useQueryParams';
import { useMemo } from 'react';
import useWidgetQuery from '../../hooks/useWidgetQuery';
import { triggerDeletedFieldsError } from '@utils/errors/triggerPageError';
import { useAtomValue } from 'jotai';
import { percentileAtom } from '@src/components/metrics-panel-hz/atoms';
import { percentileConfig } from '@src/utils/metrics/constants';
import { isAppModule } from '@src/utils/constants';

function useFetchMetrics({ wid, metrics = [] }) {
  const { interval, filter, _rKey, liveMode } = useQueryParams();
  const percentile = useAtomValue(percentileAtom);

  const allowedMetricList = useMemo(() => {
    let queryMetrics: string[] = [...metrics];

    queryMetrics.forEach((m) => {
      const invertedMetric = getPercentageMetricByKey({ metricKey: m, defaultValue: null });
      if (invertedMetric) {
        queryMetrics.push(invertedMetric);
      }
    });

    queryMetrics = [...new Set(queryMetrics)].sort();

    return queryMetrics;
  }, [metrics]);

  // Check if any metric in the list supports percentile
  const hasPercentileMetric = useMemo(() => {
    const percentileMetrics = getPercentileMetrics();
    if (!isAppModule) return false;
    return metrics.some((m) => percentileMetrics.includes(m));
  }, [metrics]);

  const options = useMemo(() => {
    const opts: any = {};

    // Add percentile to options if any metric supports it
    if (
      isAppModule &&
      hasPercentileMetric &&
      percentile &&
      percentileConfig[percentile] &&
      percentileConfig[percentile].backendPercentile
    ) {
      opts.percentile = percentileConfig[percentile].backendPercentile;
    }

    return opts;
  }, [hasPercentileMetric, percentile]);

  const payload = createQuery(
    allowedMetricList,
    `${interval.from}/${interval.to}`,
    interval.granularity,
    filter,
    options,
  );

  return useWidgetQuery({
    wid,
    queryKey: ['widget', 'metric-group', payload.queries, _rKey, percentile],
    queryFn: () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        triggerDeletedFieldsError(annotations);
        const formattedData = formatTimeseriesData({
          metrics: allowedMetricList,
          data,
          interval,
          annotations,
        });

        return { data: formattedData, totals, hasPercentileMetric, percentile };
      }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!metrics.length && !liveMode,
      staleTime: 5 * 60 * 1000, // 5m
    },
  });
}

export default useFetchMetrics;
